import { Badge } from "@/components/ui/badge";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ReleaseNoteCommit, ReleaseNoteFull } from "./types";

// Task #861 — Detail body for the change-log dialog. Splits the structured
// commit list by area so an admin can scan "what shipped where" before
// drilling into the raw markdown body. Keeps the original markdown render
// underneath so we never lose context that the generator produced.
export function ChangeLogDetailBody({
  detail,
  onTaskClick,
}: {
  detail: ReleaseNoteFull;
  onTaskClick: (taskId: number) => void;
}) {
  // Group commits by area. A single commit can touch multiple areas, in
  // which case it appears in each group — that mirrors how a reviewer
  // thinks about the release ("show me everything that touched the
  // backend"). Commits with no derived areas land in an "Uncategorized"
  // bucket so nothing is hidden.
  const groups = (() => {
    const map = new Map<string, ReleaseNoteCommit[]>();
    for (const commit of detail.commits ?? []) {
      const areas = commit.areas?.length ? commit.areas : ["uncategorized"];
      for (const area of areas) {
        const list = map.get(area) ?? [];
        list.push(commit);
        map.set(area, list);
      }
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  })();

  return (
    <div className="space-y-5">
      {detail.contributors && detail.contributors.length > 0 && (
        <div className="rounded-md border bg-muted/30 p-3" data-testid="group-changelog-contributors">
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">
            Contributors
          </div>
          <div className="flex flex-wrap gap-1">
            {detail.contributors.map((c) => (
              <Badge
                key={`${c.email}-${c.name}`}
                variant="secondary"
                className="text-xs"
                title={c.email}
                data-testid={`badge-changelog-contributor-${c.email}`}
              >
                {c.name} · {c.count}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {groups.length > 0 && (
        <div className="space-y-3" data-testid="group-changelog-areas">
          {groups.map(([area, commits]) => {
            // Build the "shipped tasks" for this area — unique task IDs
            // across the commits, with titles inlined when we resolved
            // them at generation time.
            const taskIds = Array.from(new Set(commits.flatMap((c) => c.taskIds))).sort((a, b) => a - b);
            return (
              <section key={area} data-testid={`section-changelog-area-${area}`}>
                <header className="mb-1 flex items-center justify-between">
                  <h3 className="text-sm font-semibold">
                    {area === "uncategorized" ? "Uncategorized" : area}
                    <span className="ml-2 text-xs font-normal text-muted-foreground">
                      ({commits.length} commit{commits.length === 1 ? "" : "s"})
                    </span>
                  </h3>
                </header>
                {taskIds.length > 0 && (
                  <div className="mb-1.5 flex flex-wrap gap-1" data-testid={`group-changelog-area-tasks-${area}`}>
                    {taskIds.map((tid) => {
                      const title = detail.taskTitles?.[String(tid)];
                      return (
                        <Badge
                          key={tid}
                          variant="outline"
                          className="cursor-pointer text-xs hover:bg-accent"
                          onClick={() => onTaskClick(tid)}
                          title={title ? `Task #${tid} — ${title}` : `Task #${tid}`}
                          data-testid={`badge-changelog-area-task-${area}-${tid}`}
                        >
                          {title ? `#${tid} · ${title}` : `#${tid}`}
                        </Badge>
                      );
                    })}
                  </div>
                )}
                <ul className="space-y-1 text-sm">
                  {commits.map((c) => (
                    <li key={`${area}-${c.sha}`} className="leading-snug" data-testid={`item-changelog-commit-${c.shortSha}`}>
                      <span className="font-mono text-xs text-muted-foreground">{c.shortSha}</span>{" "}
                      <span>{c.subject}</span>{" "}
                      <span className="text-xs text-muted-foreground">— {c.authorName}</span>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}

      <div>
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">
          Generated release notes
        </div>
        <article
          className="prose prose-sm dark:prose-invert max-w-none"
          data-testid="text-changelog-detail-body"
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{detail.bodyMarkdown}</ReactMarkdown>
        </article>
      </div>
    </div>
  );
}
